import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  SquaresFour, Package, ListChecks, ClockCounterClockwise, Eye, GearSix, ClockClockwise,
  ArrowLeft, Play, Pause, Rocket, Copy, Trash, ArrowClockwise, CheckCircle, XCircle,
  Timer, FloppyDisk,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { StatusBadge, StatusDot } from "../components/StatusBadge";
import { ProgressBar } from "../components/campaign/ProgressBar";
import { ConfirmDialog } from "../components/campaign/ConfirmDialog";
import { PromoSelect } from "../components/PromoSelect";
import { DataTable } from "../components/DataTable";
import { EmptyState } from "../components/EmptyState";
import { KpiSkeleton, TableSkeleton } from "../components/Skeleton";
import { SocialPreviewCard } from "../components/SocialPreviewCard";
import { ConnectionHealth } from "../components/ConnectionHealth";
import { MARKETPLACES, PLATFORM_BY_KEY } from "../platforms";
import { computeProgress, estimateCompletion, fmtDateTime, fmtDuration, fmtRelative, isPostDone, isPostError } from "../campaignUtils";

const TABS = [
  { key: "overview", Icon: SquaresFour }, { key: "products", Icon: Package },
  { key: "queue", Icon: ListChecks }, { key: "timeline", Icon: ClockCounterClockwise },
  { key: "preview", Icon: Eye }, { key: "settings", Icon: GearSix }, { key: "history", Icon: ClockClockwise },
];
const UNITS = ["minutes", "hours", "days", "weeks", "months", "years"];

export default function CampaignDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const [promo, setPromo] = useState(null);
  const [items, setItems] = useState([]);
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [live, setLive] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data: p } = await api.get(`/promotions/${id}`);
      setPromo(p);
      const [it, pv] = await Promise.all([
        api.get(`/promotions/${id}/items`).then((r) => r.data).catch(() => []),
        p.social_campaign_id
          ? api.get(`/social/posts?campaign_id=${p.social_campaign_id}`).then((r) => r.data).catch(() => [])
          : Promise.resolve([]),
      ]);
      setItems(it || []);
      setPosts(pv || []);
      const ev = await api.get("/events?limit=300").then((r) => r.data).catch(() => []);
      const postIds = new Set((it || []).map((x) => x.social_post_id));
      setEvents((ev || []).filter((e) => {
        const pl = e.payload || {};
        return pl.promotion_id === id || (pl.post_id && postIds.has(pl.post_id))
          || (p.social_campaign_id && pl.campaign_id === p.social_campaign_id);
      }));
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (currentOrg) load(); }, [currentOrg, load]);

  // Light polling for the live monitor.
  useEffect(() => {
    if (!live) return;
    timer.current = setInterval(() => load(true), 8000);
    return () => clearInterval(timer.current);
  }, [live, load]);

  const postsById = Object.fromEntries(posts.map((p) => [p.id, p]));
  // only the CURRENT execution counts (a recurring campaign adds a new batch every run)
  const runItems = promo?.run_seq ? items.filter((it) => Number(it.run_seq) === Number(promo.run_seq)) : items;
  const progress = computeProgress(runItems, postsById);
  const eta = estimateCompletion(promo, runItems, postsById);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(true); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const doPause = () => act(() => api.post(`/promotions/${id}/pause`), t("promo.detail.toast.paused"));
  const doResume = () => act(() => api.post(`/promotions/${id}/activate`), t("promo.detail.toast.resumed"));
  const doEnqueue = () => act(() => api.post(`/promotions/${id}/enqueue`), t("promo.detail.toast.enqueued"));
  const doClone = () => navigate("/promotions/new", { state: { clone: promo } });
  const doDelete = async () => {
    setConfirmDel(false);
    await act(() => api.delete(`/promotions/${id}`), t("promo.detail.toast.deleted"));
    navigate("/promotions/campaigns");
  };

  if (loading) return <div className="space-y-6" data-testid="campaign-loading"><KpiSkeleton count={4} /><TableSkeleton rows={5} cols={5} /></div>;
  if (notFound || !promo) return <EmptyState title={t("promo.detail.notFound")} />;

  const mkt = MARKETPLACES.find((m) => m.key === promo.source_platform);
  const canPause = promo.status === "active";
  const canResume = promo.status === "paused" || promo.status === "draft";

  return (
    <div className="space-y-6" data-testid="campaign-detail">
      {/* Breadcrumb + title + actions */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <Link to="/promotions/campaigns" className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="detail-back">
            <ArrowLeft size={13} /> {t("promo.nav.campaigns")}
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-medium tracking-tight">{promo.name}</h1>
            <StatusBadge status={promo.status} label={t(`promo.campaigns.status.${promo.status}`)} />
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">{mkt ? <mkt.Icon size={12} /> : null}{mkt?.label || promo.source_platform}</span>
            <span>·</span><span>{t(`promo.wizard.strategy.opt.${promo.selection_strategy}.title`)}</span>
            <span>·</span><span>{t(`promo.wizard.mode.opt.${promo.generation_mode}.title`)}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canResume && (
            <ActionBtn onClick={doResume} disabled={busy} Icon={Play} label={t("promo.detail.actions.resume")} testId="action-resume" primary />
          )}
          {canPause && (
            <ActionBtn onClick={doPause} disabled={busy} Icon={Pause} label={t("promo.detail.actions.pause")} testId="action-pause" />
          )}
          <ActionBtn onClick={doEnqueue} disabled={busy} Icon={Rocket} label={t("promo.detail.actions.enqueue")} testId="action-enqueue" />
          <ActionBtn onClick={doClone} disabled={busy} Icon={Copy} label={t("promo.detail.actions.clone")} testId="action-clone" />
          <ActionBtn onClick={() => setConfirmDel(true)} disabled={busy} Icon={Trash} label={t("common.delete")} testId="action-delete" danger />
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Internal sidebar */}
        <aside className="lg:w-52 lg:shrink-0">
          <nav className="flex gap-1 overflow-x-auto lg:flex-col" data-testid="detail-tabs">
            {TABS.map(({ key, Icon }) => (
              <button
                key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
                className={cn(
                  "flex items-center gap-2.5 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors",
                  tab === key ? "border border-brand/25 bg-brand/10 font-medium text-brand" : "text-foreground/80 hover:bg-accent/60",
                )}
              >
                <Icon size={16} weight="duotone" /> {t(`promo.detail.tabs.${key}`)}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <TabContent
                tab={tab} promo={promo} items={items} postsById={postsById} events={events}
                progress={progress} eta={eta} t={t} live={live} setLive={setLive} reload={() => load(true)}
                reloadPromo={load}
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDel} danger
        title={t("promo.detail.deleteTitle")} description={t("promo.detail.deleteDesc")}
        confirmLabel={t("common.delete")} cancelLabel={t("common.cancel")}
        onConfirm={doDelete} onCancel={() => setConfirmDel(false)}
      />
    </div>
  );
}

function ActionBtn({ onClick, disabled, Icon, label, testId, primary, danger }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
      className={cn(
        "flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50",
        primary ? "bg-primary text-primary-foreground hover:opacity-90"
          : danger ? "border border-border text-destructive hover:bg-destructive/10"
            : "border border-border hover:bg-accent",
      )}>
      <Icon size={15} weight={primary ? "fill" : "bold"} /> {label}
    </button>
  );
}

// ── Tab content ─────────────────────────────────────────────────────────────
function TabContent({ tab, promo, items, postsById, events, progress, eta, t, live, setLive, reload, reloadPromo }) {
  switch (tab) {
    case "overview":
      return <Overview promo={promo} items={items} postsById={postsById} events={events} progress={progress} eta={eta} t={t} live={live} setLive={setLive} reload={reload} />;
    case "products":
      return <Products items={items} postsById={postsById} t={t} />;
    case "queue":
      return <Queue items={items} postsById={postsById} t={t} />;
    case "timeline":
      return <Timeline items={items} postsById={postsById} t={t} />;
    case "preview":
      return <PreviewTab promo={promo} t={t} />;
    case "settings":
      return <Settings promo={promo} t={t} reloadPromo={reloadPromo} />;
    case "history":
      return <History events={events} t={t} />;
    default:
      return null;
  }
}

const isPublished = (it, byId) => byId[it.social_post_id]?.status === "published";

function Overview({ promo, items: allItems, postsById, events, progress, eta, t, live, setLive, reload }) {
  const items = promo?.run_seq ? allItems.filter((it) => Number(it.run_seq) === Number(promo.run_seq)) : allItems;
  const published = items.filter((it) => isPublished(it, postsById))
    .sort((a, b) => new Date(postsById[a.social_post_id]?.published_at || 0) - new Date(postsById[b.social_post_id]?.published_at || 0));
  const lastPublished = published[published.length - 1];
  // next = first post still WAITING (failed/partial ones are done, they will not go out again)
  const upcoming = items.filter((it) => !isPostDone(postsById[it.social_post_id]) && it.status !== "failed")
    .sort((a, b) => new Date(a.planned_publish_at) - new Date(b.planned_publish_at));
  const nextItem = upcoming[0];
  const errors = items.filter((it) => it.status === "failed" || isPostError(postsById[it.social_post_id]));
  const intervalLabel = `${promo.interval_value} ${t(`promo.wizard.interval.units.${promo.interval_unit}`)}`;

  const Stat = ({ label, value, tone }) => (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="promo-overline">{label}</div>
      <div className={cn("mt-1 text-2xl font-medium tabular-nums", tone)}>{value}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="tab-content-overview">
      {/* Live header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <StatusDot status={promo.status === "active" ? "active" : promo.status} />
          <span className="font-medium">{t(`promo.campaigns.status.${promo.status}`)}</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            {t("promo.detail.live")}
            <Switch checked={live} onCheckedChange={setLive} data-testid="live-toggle" />
          </label>
          <button onClick={reload} className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs hover:bg-accent" data-testid="detail-refresh">
            <ArrowClockwise size={13} /> {t("promo.conn.refresh")}
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="rounded-lg border border-border bg-card p-5" data-testid="progress-card">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">{t("promo.detail.progress")}</h3>
          <span className="text-2xl font-medium tabular-nums text-brand">{progress.pct}%</span>
        </div>
        <ProgressBar value={progress.pct} tone={promo.status === "completed" ? "success" : "brand"} className="mt-3" />
        <div className="mt-4 grid grid-cols-3 gap-4">
          <Stat label={t("promo.detail.totalProducts")} value={progress.totalProducts} />
          <Stat label={t("promo.detail.completed")} value={progress.publishedProducts} tone="text-success" />
          <Stat label={t("promo.detail.remaining")} value={progress.remainingProducts} />
        </div>
        {promo.progress?.groups?.total > 0 && (() => {
          const g = promo.progress.groups;   // Facebook groups track (03/11/2026)
          const done = g.published + g.pending_approval + (g.declined || 0) + g.failed + (g.cancelled || 0);
          return (
            <div className="mt-4 border-t border-border/60 pt-4 text-sm" data-testid="groups-progress">
              <div className="flex items-center justify-between">
                <span className="font-medium">{t("promo.detail.groups.title")}</span>
                <span className="tabular-nums text-muted-foreground">{done} / {g.total}</span>
              </div>
              <ProgressBar value={g.total ? Math.round((done / g.total) * 100) : 0} className="mt-2" />
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs tabular-nums">
                <span className="text-success">{t("promo.detail.groups.published")}: {g.published}</span>
                <span className="text-brand">{t("promo.detail.groups.approval")}: {g.pending_approval}</span>
                {g.declined > 0 && <span className="text-muted-foreground">{t("promo.detail.groups.declined")}: {g.declined}</span>}
                <span className="text-destructive">{t("promo.detail.groups.failed")}: {g.failed}</span>
                <span className="text-muted-foreground">{t("promo.detail.groups.queued")}: {g.pending}</span>
                {g.cancelled > 0 && <span className="text-muted-foreground">{t("promo.detail.groups.cancelled")}: {g.cancelled}</span>}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Estimated completion */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label={t("promo.detail.interval")} value={intervalLabel} />
        <Stat label={t("promo.detail.eta")} value={fmtDuration(eta.etaMs)} />
        <Stat label={t("promo.detail.endDate")} value={fmtDateTime(eta.endDate)} />
        <Stat label={t("promo.detail.nextPublish")} value={fmtDateTime(eta.nextPublishAt)} />
      </div>

      {/* Latest / Next / Errors */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <MonitorCard Icon={CheckCircle} tone="text-success" title={t("promo.detail.lastPost")}>
          {lastPublished ? (
            <PostLine it={lastPublished} byId={postsById} time={postsById[lastPublished.social_post_id]?.published_at} />
          ) : <Muted>{t("promo.detail.noPublished")}</Muted>}
        </MonitorCard>
        <MonitorCard Icon={Timer} tone="text-brand" title={t("promo.detail.nextPost")}>
          {nextItem ? <PostLine it={nextItem} byId={postsById} time={nextItem.planned_publish_at} /> : <Muted>{t("promo.detail.noQueue")}</Muted>}
        </MonitorCard>
        <MonitorCard Icon={XCircle} tone="text-destructive" title={t("promo.detail.errors")}>
          {errors.length ? <div className="text-2xl font-medium tabular-nums text-destructive">{errors.length}</div> : <Muted>{t("promo.detail.noErrors")}</Muted>}
        </MonitorCard>
      </div>

      {/* Recent events */}
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-3 text-sm font-medium">{t("promo.detail.recentEvents")}</h3>
        {events.length === 0 ? <Muted>{t("promo.detail.noEvents")}</Muted> : (
          <div className="space-y-2">
            {events.slice(0, 6).map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`event-${e.id}`}>
                <span className="flex items-center gap-2 truncate"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" /><span className="truncate font-mono text-xs">{e.type}</span></span>
                <span className="shrink-0 text-xs text-muted-foreground">{fmtRelative(e.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConnectionHealth title={t("promo.conn.status")} showReconnect={false} />
    </div>
  );
}

const Muted = ({ children }) => <p className="text-sm text-muted-foreground">{children}</p>;
function MonitorCard({ Icon, tone, title, children }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="mb-2 flex items-center gap-2"><Icon size={15} className={tone} /><span className="promo-overline">{title}</span></div>
      {children}
    </div>
  );
}
function PostLine({ it, byId, time }) {
  const meta = PLATFORM_BY_KEY[it.platform];
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-sm font-medium">{meta && <meta.Icon size={13} />}{it.product_external_id}</div>
      <div className="text-xs text-muted-foreground">{meta?.label} · {fmtDateTime(time)}</div>
    </div>
  );
}

function Products({ items, postsById, t }) {
  const groups = {};
  items.forEach((it) => { (groups[it.product_external_id] ||= []).push(it); });
  const rows = Object.entries(groups);
  if (!rows.length) return <EmptyState Icon={Package} title={t("promo.detail.emptyProducts")} />;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="tab-content-products">
      {rows.map(([pid, its]) => (
        <div key={pid} className="rounded-lg border border-border bg-card p-4" data-testid={`product-${pid}`}>
          <div className="text-sm font-medium">{pid}</div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {its.map((it) => {
              const meta = PLATFORM_BY_KEY[it.platform];
              const st = postsById[it.social_post_id]?.status || it.status;
              return (
                <span key={it.id} className="flex items-center gap-1 rounded-md border border-border bg-accent/40 px-2 py-1 text-xs">
                  {meta && <meta.Icon size={11} />}<StatusDot status={st} />
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Queue({ items, postsById, t }) {
  const rows = [...items].sort((a, b) => new Date(a.planned_publish_at) - new Date(b.planned_publish_at));
  const cols = [
    { key: "product", label: t("promo.detail.col.product"), render: (r) => r.product_external_id },
    { key: "platform", label: t("promo.detail.col.platform"), render: (r) => {
      const m = PLATFORM_BY_KEY[r.platform];
      return <span className="flex items-center gap-1.5">{m && <m.Icon size={13} />}{m?.label || r.platform}</span>;
    } },
    { key: "planned", label: t("promo.detail.col.planned"), render: (r) => fmtDateTime(r.planned_publish_at) },
    { key: "status", label: t("common.status"), render: (r) => {
      const st = postsById[r.social_post_id]?.status || r.status;
      return <StatusBadge status={st} label={st} />;
    } },
  ];
  return (
    <div data-testid="tab-content-queue">
      <DataTable columns={cols} rows={rows} empty={<EmptyState Icon={ListChecks} title={t("promo.detail.emptyQueue")} description={t("promo.detail.emptyQueueDesc")} />} />
    </div>
  );
}

function Timeline({ items, postsById, t }) {
  const rows = [...items].sort((a, b) => new Date(b.planned_publish_at) - new Date(a.planned_publish_at));
  if (!rows.length) return <EmptyState Icon={ClockCounterClockwise} title={t("promo.detail.emptyTimeline")} />;
  return (
    <div className="relative space-y-4 border-l border-border pl-6" data-testid="tab-content-timeline">
      {rows.map((it) => {
        const st = postsById[it.social_post_id]?.status || it.status;
        const meta = PLATFORM_BY_KEY[it.platform];
        return (
          <div key={it.id} className="relative" data-testid={`timeline-item-${it.id}`}>
            <span className="absolute -left-[27px] top-1"><StatusDot status={st} /></span>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm font-medium">{meta && <meta.Icon size={13} />}{it.product_external_id}</div>
              <StatusBadge status={st} label={st} />
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">{meta?.label} · {fmtDateTime(it.planned_publish_at)}</div>
          </div>
        );
      })}
    </div>
  );
}

function PreviewTab({ promo, t }) {
  const [state, setState] = useState({ result: null, loading: false });
  const run = async () => {
    setState({ result: null, loading: true });
    try {
      const { data } = await api.post("/promotions/preview", { ...promo, preview_platforms: promo.platforms });
      setState({ result: data, loading: false });
    } catch (e) { setState({ result: null, loading: false }); toast.error(apiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);
  return (
    <div className="space-y-4" data-testid="tab-content-preview">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium tracking-tight">{t("promo.wizard.preview.title")}</h3>
        <button onClick={run} disabled={state.loading} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm hover:bg-accent disabled:opacity-50" data-testid="preview-regenerate">
          <ArrowClockwise size={14} className={state.loading ? "animate-spin" : ""} /> {t("promo.wizard.preview.regen")}
        </button>
      </div>
      {state.loading ? (
        <div className="promo-beam rounded-lg border border-border bg-card p-10 text-center text-sm text-muted-foreground">{t("promo.wizard.preview.generating")}</div>
      ) : !state.result?.product ? (
        <EmptyState Icon={Eye} title={t("promo.wizard.preview.emptyTitle")} description={t("promo.wizard.preview.emptyDesc")} />
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm"><span className="text-muted-foreground">{t("promo.wizard.preview.product")}: </span><span className="font-medium">{state.result.product.title}</span></div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {state.result.previews.map((pv, i) => <SocialPreviewCard key={pv.platform} preview={pv} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function Settings({ promo, t, reloadPromo }) {
  const [f, setF] = useState({
    name: promo.name, generation_mode: promo.generation_mode, selection_strategy: promo.selection_strategy,
    language: promo.language, interval_value: promo.interval_value, interval_unit: promo.interval_unit,
    max_items: promo.max_items, auto_create_campaign: !!promo.auto_create_campaign,
  });
  const [saving, setSaving] = useState(false);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/promotions/${promo.id}`, { ...f, interval_value: Number(f.interval_value), max_items: Number(f.max_items) });
      toast.success(t("promo.detail.toast.saved"));
      await reloadPromo(true);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const strat = ["sequential", "random", "best_sellers", "newest", "ai_decide"];
  const modes = ["original", "ai_enhance", "ai_complete"];
  const langs = [["en", "English"], ["it", "Italiano"], ["es", "Español"], ["fr", "Français"], ["de", "Deutsch"], ["pt", "Português"]];
  return (
    <div className="max-w-xl space-y-5" data-testid="tab-content-settings">
      <div className="space-y-2"><Label>{t("promo.wizard.source.name")}</Label>
        <Input value={f.name} onChange={(e) => set({ name: e.target.value })} className="bg-card" data-testid="settings-name" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>{t("promo.wizard.steps.strategy")}</Label>
          <PromoSelect value={f.selection_strategy} onChange={(v) => set({ selection_strategy: v })} testId="settings-strategy"
            options={strat.map((s) => ({ value: s, label: t(`promo.wizard.strategy.opt.${s}.title`) }))} /></div>
        <div className="space-y-2"><Label>{t("promo.wizard.steps.mode")}</Label>
          <PromoSelect value={f.generation_mode} onChange={(v) => set({ generation_mode: v })} testId="settings-mode"
            options={modes.map((m) => ({ value: m, label: t(`promo.wizard.mode.opt.${m}.title`) }))} /></div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2"><Label>{t("promo.wizard.interval.every")}</Label>
          <Input type="number" min={1} value={f.interval_value} onChange={(e) => set({ interval_value: e.target.value })} className="bg-card" data-testid="settings-interval-value" /></div>
        <div className="space-y-2"><Label>{t("promo.wizard.interval.unit")}</Label>
          <PromoSelect value={f.interval_unit} onChange={(v) => set({ interval_unit: v })} testId="settings-interval-unit"
            options={UNITS.map((u) => ({ value: u, label: t(`promo.wizard.interval.units.${u}`) }))} /></div>
        <div className="space-y-2"><Label>{t("promo.wizard.product.max")}</Label>
          <Input type="number" min={1} value={f.max_items} onChange={(e) => set({ max_items: e.target.value })} className="bg-card" data-testid="settings-max" /></div>
      </div>
      <div className="max-w-xs space-y-2"><Label>{t("promo.wizard.steps.language")}</Label>
        <PromoSelect value={f.language} onChange={(v) => set({ language: v })} testId="settings-language"
          options={langs.map(([v, l]) => ({ value: v, label: l }))} /></div>
      <label className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
        <span className="text-sm font-medium">{t("promo.wizard.start.autoCampaign")}</span>
        <Switch checked={f.auto_create_campaign} onCheckedChange={(v) => set({ auto_create_campaign: v })} data-testid="settings-auto-campaign" />
      </label>
      <button onClick={save} disabled={saving}
        className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50" data-testid="settings-save">
        <FloppyDisk size={16} /> {t("common.save")}
      </button>
    </div>
  );
}

function History({ events, t }) {
  const cols = [
    { key: "type", label: t("m2.type"), render: (r) => <span className="font-mono text-xs">{r.type}</span> },
    { key: "actor", label: t("m2.actor"), render: (r) => <span className="text-xs text-muted-foreground">{r.actor_email || "system"}</span> },
    { key: "time", label: t("m2.time"), render: (r) => fmtDateTime(r.created_at) },
  ];
  return (
    <div data-testid="tab-content-history">
      <DataTable columns={cols} rows={events} empty={<EmptyState Icon={ClockClockwise} title={t("promo.detail.noEvents")} />} />
    </div>
  );
}
